import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Plus, Trash2, Loader2, UserCheck } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  googleDocsIntegrationService,
  type UserForApprovers,
} from '@/features/google-docs-integration/services/google-docs-integration.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/google-docs-config')({
  component: GoogleDocsConfigPage,
});

function GoogleDocsConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['google-docs-integration-config'],
    queryFn: () => googleDocsIntegrationService.getConfig(),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['google-docs-integration-users'],
    queryFn: () => googleDocsIntegrationService.listUsers(),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { viewerEmails?: string[]; approverUserIds?: string[] }) =>
      googleDocsIntegrationService.updateConfig(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['google-docs-integration-config'] });
      if (variables.approverUserIds !== undefined) {
        toast({ title: 'Configuração salva', description: 'Aprovadores foram atualizados.' });
      } else {
        toast({ title: 'Configuração salva', description: 'Os e-mails de edição foram atualizados.' });
      }
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao salvar',
        variant: 'destructive',
      });
    },
  });

  const viewerEmails = Array.isArray(config?.viewerEmails) ? config.viewerEmails : [];
  const approverUserIds = Array.isArray(config?.approverUserIds) ? config.approverUserIds : [];

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (viewerEmails.includes(email)) {
      toast({ title: 'E-mail já está na lista', variant: 'destructive' });
      return;
    }
    setNewEmail('');
    updateMutation.mutate({ viewerEmails: [...viewerEmails, email], approverUserIds });
  };

  const handleRemoveEmail = (email: string) => {
    updateMutation.mutate({
      viewerEmails: viewerEmails.filter((e) => e !== email),
      approverUserIds,
    });
  };

  const handleToggleApprover = (userId: string, checked: boolean) => {
    const next = checked
      ? [...approverUserIds, userId]
      : approverUserIds.filter((id) => id !== userId);
    updateMutation.mutate({ viewerEmails, approverUserIds: next });
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <PermissionGate permission={Permission.CONTRACTS_MANAGE}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Geração de contratos</h1>
          <p className="text-muted-foreground mt-2">
            Configure e-mails com permissão de edição nos documentos e usuários que podem aprovar o contrato e enviar para assinatura no D4Sign.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              E-mails com permissão de edição
            </CardTitle>
            <CardDescription>
              Adicione os e-mails que poderão abrir, visualizar e editar os documentos criados na pasta configurada no servidor.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email@empresa.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                className="max-w-sm"
              />
              <Button onClick={handleAddEmail} disabled={updateMutation.isPending || !newEmail.trim()}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar
              </Button>
            </div>
            {viewerEmails.length > 0 ? (
              <ul className="space-y-2 rounded-lg border p-4">
                {viewerEmails.map((email) => (
                  <li key={email} className="flex items-center justify-between gap-2 py-1">
                    <span className="text-sm">{email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmail(email)}
                      disabled={updateMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum e-mail configurado. Os documentos criados só serão acessíveis pela Service Account e por quem
                você compartilhar manualmente no Drive (como editor).
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Usuários que podem aprovar contrato para assinatura
            </CardTitle>
            <CardDescription>
              Apenas estes usuários poderão enviar o documento para assinatura no D4Sign. Ao preencher o formulário, o documento é gerado e os aprovadores são notificados; somente eles verão o botão para enviar para assinatura.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando usuários...
              </div>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                Nenhum usuário ativo no tenant.
              </p>
            ) : (
              <ul className="space-y-2 rounded-lg border p-4 max-h-80 overflow-y-auto">
                {users.map((u: UserForApprovers) => (
                  <li key={u.id} className="flex items-center gap-3 py-1.5">
                    <Checkbox
                      id={`approver-${u.id}`}
                      checked={approverUserIds.includes(u.id)}
                      onCheckedChange={(checked) => handleToggleApprover(u.id, !!checked)}
                      disabled={updateMutation.isPending}
                    />
                    <Label htmlFor={`approver-${u.id}`} className="flex-1 cursor-pointer text-sm font-normal">
                      {u.name} <span className="text-muted-foreground">({u.email})</span>
                    </Label>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
